import { useMemo, useState } from "react";
import { Badge, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from "@tanstack/react-table";
import {
    formatCurrencyToLocale,
    formatQuantityToLocale,
    formatValueToLocale,
} from "../../../../utils/method";
import "./InvoiceOverviewSection.scss";

const columnHelper = createColumnHelper();

/** Measured amount for one position (aufmass qty × unit price). */
const calcMeasuredAmount = (pos) =>
    parseFloat(pos.aufmassQuantity || 0) * parseFloat(pos.unitPrice || 0);

const InvoiceOverviewSection = () => {
    const { t } = useTranslation();
    const { supplierInfo, loading } = useSelector(
        (state) => state.supplierInfo,
    );
    const { quantities } = supplierInfo || { quantities: {} };
    const isV2Schema = !supplierInfo?.isStored || !!supplierInfo?.schemaVersion;
    const lvQuantityWarningPercent =
        supplierInfo?.projectTicketCategory?.lvQuantityWarningPercent ?? 85;

    const [collapsedGroups, setCollapsedGroups] = useState(new Set());
    const [positionFilter, setPositionFilter] = useState("exceeding");

    const toggleGroup = (lvName) => {
        setCollapsedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(lvName)) next.delete(lvName);
            else next.add(lvName);
            return next;
        });
    };

    const columns = useMemo(
        () => {
            const baseColumns = [
                columnHelper.accessor("itemNumber", {
                    header: t("invoice.item-number"),
                    size: 90,
                }),
                columnHelper.accessor("shortText", {
                    header: t("supplier-info.position-description"),
                    cell: (info) => info.getValue() || "",
                    size: 200,
                    meta: { isDescription: true },
                }),
                columnHelper.accessor("lvQuantity", {
                    header: t("supplier-info.lv-quantity"),
                    cell: (info) => {
                        const v = info.getValue();
                        const unit = info.row.original.unitTag || "";
                        return v != null && v > 0
                            ? `${formatQuantityToLocale(v)} ${unit}`.trim()
                            : "-";
                    },
                    size: 105,
                    meta: { align: "right" },
                }),
                columnHelper.accessor("quantity", {
                    header: t("invoice.invoice-quantity"),
                    cell: (info) => {
                        const v = info.getValue();
                        const unit = info.row.original.unitTag || "";
                        return v != null
                            ? `${formatQuantityToLocale(v)} ${unit}`.trim()
                            : "";
                    },
                    size: 105,
                    meta: { align: "right" },
                }),
                columnHelper.accessor("aufmassQuantity", {
                    header: t("aufmass.quantity"),
                    cell: (info) => {
                        const v = info.getValue();
                        const unit = info.row.original.unitTag || "";
                        return v != null
                            ? `${formatQuantityToLocale(v)} ${unit}`.trim()
                            : "";
                    },
                    size: 105,
                    meta: { align: "right" },
                }),
                columnHelper.accessor("deviation", {
                    header: t("supplier-info.quantity-deviation"),
                    cell: (info) => {
                        const value = parseFloat(info.getValue() || 0);
                        const unit = info.row.original.unitTag || "";
                        const formatted = formatQuantityToLocale(Math.abs(value));
                        const aufmass = parseFloat(info.row.original.aufmassQuantity || 0);
                        const lv = parseFloat(info.row.original.lvQuantity || 0);
                        const pct = lv > 0 ? (aufmass / lv) * 100 : (aufmass > 0 ? 100 : 0);
                        let bg = "secondary";
                        let prefix = "";
                        if (value > 0.01) prefix = "+";
                        else if (value < -0.01) prefix = "-";
                        if (pct > 100.01) bg = "danger";
                        else if (pct >= 99.99) bg = "success";
                        else if (pct >= lvQuantityWarningPercent) bg = "warning";
                        return (
                            <Badge
                                bg={bg}
                                className="ios-badge-sm"
                            >
                                {prefix}{formatted}{unit ? ` ${unit}` : ""}
                            </Badge>
                        );
                    },
                    size: 110,
                    meta: { align: "right" },
                }),
            ];

            if (isV2Schema) {
                baseColumns.push(
                    columnHelper.accessor(
                        (row) => {
                            const lv = parseFloat(row.lvQuantity || 0);
                            const auf = parseFloat(row.aufmassQuantity || 0);
                            // % of LV quantity that has been measured
                            return lv > 0 ? (auf / lv) * 100 : null;
                        },
                        {
                            id: "deviationPercent",
                            header: t("supplier-info.deviation-percent"),
                            cell: (info) => {
                                const v = info.getValue();
                                if (v == null) return "-";
                                let bg = "secondary";
                                if (v > 100.01) bg = "danger";
                                else if (v >= 99.99) bg = "success";
                                else if (v >= lvQuantityWarningPercent) bg = "warning";
                                return (
                                    <Badge bg={bg} className="ios-badge-xs">
                                        {formatValueToLocale(v)}%
                                    </Badge>
                                );
                            },
                            size: 90,
                            meta: { align: "right" },
                        },
                    ));
            }

            baseColumns.push(
                columnHelper.accessor("totalPrice", {
                    id: "invoiceAmount",
                    header: t("supplier-info.invoice-amount"),
                    cell: (info) => formatCurrencyToLocale(info.getValue() || 0),
                    size: 120,
                    meta: { align: "right" },
                }),
                columnHelper.accessor((row) => calcMeasuredAmount(row), {
                        id: "checkedAmount",
                        header: t("supplier-info.checked-amount"),
                        cell: (info) =>
                            formatCurrencyToLocale(info.getValue() || 0),
                        size: 120,
                        meta: { align: "right" },
                    },
                ),
                columnHelper.accessor(
                    (row) =>
                        calcMeasuredAmount(row) - parseFloat(row.totalPrice || 0),
                    {
                        id: "correction",
                        header: t("supplier-info.correction-amount"),
                        cell: (info) => {
                            const v = info.getValue() || 0;
                            const hasDeviation = Math.abs(v) > 0.01;
                            return (
                                <span
                                    className={hasDeviation ? (v > 0 ? "ios-correction--positive" : "ios-correction--negative") : ""}
                                >
                                    {formatCurrencyToLocale(v)}
                                </span>
                            );
                        },
                        size: 120,
                        meta: { align: "right" },
                    },
                ),
                columnHelper.accessor("comments", {
                    header: t("supplier-info.comments"),
                    cell: (info) => info.getValue() || "",
                    size: 260,
                    meta: { isComments: true },
                }),
            );

            return baseColumns;
        },
        [isV2Schema, t, lvQuantityWarningPercent],
    );

    // Group positions by LV, sort Nachtrag last, flatten with sentinel group-header rows
    const tableData = useMemo(() => {
        const allPositions = quantities?.invoice?.positions;
        if (!allPositions?.length) return [];

        const positions =
            isV2Schema && positionFilter === "exceeding"
                ? allPositions.filter((pos) => {
                    const lv = parseFloat(pos.lvQuantity || 0);
                    const auf = parseFloat(pos.aufmassQuantity || 0);
                    return auf > lv;
                })
                : allPositions;

        if (!positions.length) return [];

        const grouped = {};
        positions.forEach((pos) => {
            const key = pos.lvName || t("supplier-info.unknown-lv");
            if (!grouped[key]) {
                grouped[key] = { positions: [], isNachtraege: pos.isNachtraege ?? false };
            }
            grouped[key].positions.push(pos);
            if (pos.isNachtraege) {
                grouped[key].isNachtraege = true;
            }
        });

        const sortedLvNames = Object.keys(grouped).sort((a, b) => {
            const aIsNachtrag = grouped[a].isNachtraege;
            const bIsNachtrag = grouped[b].isNachtraege;
            if (aIsNachtrag !== bIsNachtrag) return aIsNachtrag ? 1 : -1;
            return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
        });

        const rows = [];
        sortedLvNames.forEach((lvName) => {
            const lvPositions = grouped[lvName].positions;
            const totalMeasuredAmount = lvPositions.reduce(
                (sum, pos) => sum + calcMeasuredAmount(pos),
                0,
            );
            rows.push({
                _isGroupHeader: true,
                _lvName: lvName,
                _groupCount: lvPositions.length,
                _totalMeasuredAmount: totalMeasuredAmount,
                id: `group::${lvName}`,
            });
            const sorted = [...grouped[lvName].positions].sort((a, b) =>
                String(a.itemNumber || "").localeCompare(
                    String(b.itemNumber || ""),
                    undefined,
                    { numeric: true, sensitivity: "base" },
                ),
            );
            sorted.forEach((pos) =>
                rows.push({ ...pos, _isGroupHeader: false, _lvName: lvName }),
            );
        });
        return rows;
    }, [isV2Schema, quantities, positionFilter, t]);

    const table = useReactTable({
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => (row._isGroupHeader ? row.id : String(row.id)),
    });

    // Summary totals
    const totalData = useMemo(() => {
        if (!quantities) return null;
        const invoice = quantities?.invoice || {};
        const aufmass = quantities?.aufmass || {};
        return {
            invoiceQuantity: invoice?.total || 0,
            aufmassQuantity: aufmass?.total || 0,
            deviation: aufmass?.total - invoice?.total,
            deviationPercentage: aufmass?.totalDeviationPercentage,
            hasMismatch: aufmass?.hasMismatch,
        };
    }, [quantities]);

    const renderSummary = () => {
        if (!totalData) return null;
        const deviationClass = totalData.hasMismatch
            ? totalData.deviation > 0
                ? "text-warning"
                : "text-danger"
            : "text-success";
        return (
            <div className="mt-3 p-3 border rounded bg-light">
                <h6 className="mb-3">{t("supplier-info.amounts-summary")}</h6>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <div className="mb-2">
                            <strong>
                                {t("supplier-info.invoice-amount")}:
                            </strong>{" "}
                            {formatCurrencyToLocale(totalData.invoiceQuantity)}
                        </div>
                        <div className="mb-2">
                            <strong>
                                {t("supplier-info.checked-amount")}:
                            </strong>{" "}
                            {formatCurrencyToLocale(totalData.aufmassQuantity)}
                        </div>
                    </div>
                    <div>
                        <div className={`mb-2 ${deviationClass}`}>
                            <strong>
                                {t("supplier-info.deviation-amount")}:
                            </strong>{" "}
                            {totalData.deviation > 0 ? "+" : ""}
                            {formatCurrencyToLocale(totalData.deviation)}
                        </div>
                        <div className={`mb-0 ${deviationClass}`}>
                            <strong>
                                {t("supplier-info.deviation-percentage")}:
                            </strong>{" "}
                            {formatValueToLocale(totalData.deviationPercentage)}
                            %
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Card className="mb-4">
            <Card.Header>
                <h5 className="mb-0">{t("supplier-info.invoice-overview")}</h5>
            </Card.Header>
            <Card.Body className="p-0">
                {loading ? (
                    <div className="text-center py-4">
                        <div
                            className="spinner-border text-primary"
                            role="status"
                        >
                            <span className="visually-hidden">
                                {t("common.loading")}
                            </span>
                        </div>
                        <p className="mt-2">{t("common.loading")}</p>
                    </div>
                ) : (
                    <div>
                        {isV2Schema && (
                            <div className="d-flex align-items-center justify-content-end gap-2 px-3 py-2 border-bottom ios-filter-bar">
                                <span className="text-muted ios-filter-label">
                                    {t("hierarchical-positions.position-filters")}:
                                </span>
                                <div className="btn-group btn-group-sm" role="group">
                                    <button
                                        type="button"
                                        className={`btn btn-sm ios-filter-btn ${positionFilter === "all"
                                            ? "btn-primary"
                                            : "btn-outline-secondary"
                                            }`}
                                        onClick={() => setPositionFilter("all")}
                                    >
                                        {t("hierarchical-positions.filter-show-all")}
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn btn-sm ios-filter-btn ${positionFilter === "exceeding"
                                            ? "btn-danger"
                                            : "btn-outline-secondary"
                                            }`}
                                        onClick={() => setPositionFilter("exceeding")}
                                    >
                                        {t("hierarchical-positions.filter-exceeding-lv")}
                                    </button>
                                </div>
                            </div>
                        )}
                        <div className="ios-wrapper">
                            <table className="ios-table">
                                <thead>
                                    {table.getHeaderGroups().map((hg) => (
                                        <tr key={hg.id}>
                                            {hg.headers.map((header) => (
                                                <th
                                                    key={header.id}
                                                    className={`ios-th${header.column.columnDef.meta?.align === "right" ? " ios-th--right" : ""}`}
                                                    style={{
                                                        width: header.column.getSize(),
                                                        minWidth: header.column.getSize(),
                                                    }}
                                                >
                                                    {flexRender(
                                                        header.column.columnDef
                                                            .header,
                                                        header.getContext(),
                                                    )}
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody>
                                    {!tableData.length ? (
                                        <tr>
                                            <td
                                                colSpan={columns.length}
                                                className="ios-empty-td"
                                            >
                                                {t(
                                                    "supplier-info.no-positions-message",
                                                )}
                                            </td>
                                        </tr>
                                    ) : (
                                        (() => {
                                            let posIdx = 0;
                                            return table
                                                .getRowModel()
                                                .rows.map((row) => {
                                                    const orig = row.original;

                                                    if (orig._isGroupHeader) {
                                                        const isCollapsed =
                                                            collapsedGroups.has(
                                                                orig._lvName,
                                                            );
                                                        return (
                                                            <tr
                                                                key={row.id}
                                                                className="ios-group-tr"
                                                                onClick={() =>
                                                                    toggleGroup(
                                                                        orig._lvName,
                                                                    )
                                                                }
                                                            >
                                                                <td
                                                                    colSpan={
                                                                        columns.length
                                                                    }
                                                                    className="ios-group-td"
                                                                >
                                                                    <div className="ios-group-header-inner">
                                                                        <span className="ios-group-header-title">
                                                                            <span
                                                                                className={`ios-chevron${isCollapsed ? " ios-chevron--collapsed" : ""}`}
                                                                            >
                                                                                ▼
                                                                            </span>
                                                                            {orig._lvName}
                                                                            <span className="ios-count-label">
                                                                                ({orig._groupCount})
                                                                            </span>
                                                                        </span>
                                                                        <span className="ios-group-total">
                                                                            <span className="ios-group-total-label">
                                                                                {t("supplier-info.checked-amount")}:
                                                                            </span>
                                                                            {formatCurrencyToLocale(
                                                                                orig._totalMeasuredAmount,
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    }

                                                    if (
                                                        collapsedGroups.has(
                                                            orig._lvName,
                                                        )
                                                    )
                                                        return null;

                                                    const isEven =
                                                        posIdx++ % 2 === 0;

                                                    return (
                                                        <tr
                                                            key={row.id}
                                                            className={`ios-data-row ${isEven ? "ios-data-row--even" : "ios-data-row--odd"}`}
                                                        >
                                                            {row
                                                                .getVisibleCells()
                                                                .map((cell) => {
                                                                    const meta =
                                                                        cell
                                                                            .column
                                                                            .columnDef
                                                                            .meta;
                                                                    const tdClass = [
                                                                        "ios-td",
                                                                        meta?.isComments ? "ios-td--comments" : "",
                                                                        meta?.isDescription ? "ios-td--description" : "",
                                                                        meta?.align === "right" ? "ios-td--right" : "",
                                                                    ].filter(Boolean).join(" ");
                                                                    return (
                                                                        <td
                                                                            key={
                                                                                cell.id
                                                                            }
                                                                            className={tdClass}
                                                                        >
                                                                            {flexRender(
                                                                                cell
                                                                                    .column
                                                                                    .columnDef
                                                                                    .cell,
                                                                                cell.getContext(),
                                                                            )}
                                                                        </td>
                                                                    );
                                                                })}
                                                        </tr>
                                                    );
                                                });
                                        })()
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div className="p-3">{renderSummary()}</div>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

export default InvoiceOverviewSection;
